import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const SUB_PAGES = [
  { slug: "team", label: "Team", blurb: "Purpose, values, leadership and our people." },
  { slug: "timeline", label: "Timeline", blurb: "Five decades, in milestones." },
  { slug: "gallery", label: "Gallery", blurb: "Yards, dispatch and operations." },
];

/**
 * AboutSubnav — premium contextual sub-navigation for About pages.
 * Shown on every /about/* page; highlights the active subpage and
 * offers quick navigation to siblings.
 */
export const AboutSubnav = () => {
  const { pathname } = useLocation();
  const current = pathname.split("/")[2];
  return (
    <nav
      data-testid="about-subnav"
      className="sticky top-[68px] z-30 border-b border-black/5 bg-white/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-[1400px] items-center gap-1 overflow-x-auto px-6 py-3 lg:px-10">
        <Link
          to="/about/team"
          className="mr-3 shrink-0 font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]"
        >
          / About
        </Link>
        {SUB_PAGES.map((p) => (
          <Link
            key={p.slug}
            to={`/about/${p.slug}`}
            data-testid={`about-subnav-${p.slug}`}
            className={`shrink-0 rounded-full px-4 py-2 text-[12px] font-medium transition-all duration-300 ${
              current === p.slug
                ? "bg-[#1c1c1e] text-white"
                : "text-[#4a4a4e] hover:bg-black/5 hover:text-[#1c1c1e]"
            }`}
          >
            {p.label}
          </Link>
        ))}
      </div>
    </nav>
  );
};

/**
 * Premium cinematic hero — used as the lead block on every About subpage.
 */
export const AboutHero = ({ kicker, title, subtitle, accent = "" }) => (
  <section className="relative overflow-hidden border-b border-black/5 bg-gradient-to-b from-[#f7f7f5] to-white py-24 md:py-32">
    <div className="absolute inset-0 bg-grid opacity-40" />
    <div className="absolute -right-32 top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-[#fff1e8] opacity-60 blur-3xl" />
    <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#e85a1c] via-[#e85a1c]/40 to-transparent" />
    <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
      <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
        {kicker}
      </div>
      <h1 className="mt-5 font-display text-5xl uppercase leading-[0.86] tracking-tighter text-[#1c1c1e] md:text-7xl lg:text-[100px]">
        {title}
        {accent ? <span className="text-[#e85a1c]">{accent}</span> : null}
      </h1>
      {subtitle ? (
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#4a4a4e] md:text-lg">
          {subtitle}
        </p>
      ) : null}
    </div>
  </section>
);

/**
 * Bottom card that auto-suggests the next About subpage.
 */
export const AboutNextCard = ({ currentSlug }) => {
  const idx = SUB_PAGES.findIndex((p) => p.slug === currentSlug);
  const next = SUB_PAGES[(idx + 1) % SUB_PAGES.length];
  return (
    <section className="border-b border-black/5 bg-[#f7f7f5] py-20">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <Link
          to={`/about/${next.slug}`}
          data-testid={`about-next-${next.slug}`}
          className="group flex flex-col items-start justify-between gap-6 rounded-2xl border border-black/5 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#e85a1c]/40 hover:shadow-lux-hover md:flex-row md:items-center md:p-10"
        >
          <div>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              Next chapter
            </div>
            <h3 className="mt-3 font-display text-4xl uppercase tracking-tighter text-[#1c1c1e] md:text-5xl">
              {next.label}
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-[#4a4a4e]">
              {next.blurb}
            </p>
          </div>
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#e85a1c] text-white transition-all duration-300 group-hover:translate-x-1">
            <ArrowRight size={18} />
          </span>
        </Link>
      </div>
    </section>
  );
};
